
import { observer } from "mobx-react-lite";
import { useState, useCallback } from "react";
import { useSearchParams } from "@/lib/router-compat";
import { useRouter } from "@/lib/router-compat";
import Link from "@/components/ui/link-compat";
import { CalendarDays, User, UserCheck, Handshake, Pencil, AlignLeft, CircleDot, Flag, FolderKanban } from "lucide-react";
import { CrmTaskStatus, CrmTaskPriority, ContactType } from "@/types/crm";
import { useTask, useDeleteTask } from "@/hooks/useTasks";
import { useTaskStore, useUserStore, useContactStore, useDealStore, useDealTypeStore, useProjectStore } from "@/stores/StoreProvider";
import { LinkedRecordCard } from "@/components/ui/linked-record-card";
import { InlineField } from "@/components/ui/inline-field";
import { InlineSelectField } from "@/components/ui/inline-select-field";
import { InlineDateField } from "@/components/ui/inline-date-field";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useDeferredMount } from '@/hooks/useDeferredMount';
import { useEntityTimeline } from "@/hooks/useTimeline";
import { useUpdateNote, useDeleteNote } from "@/hooks/useNotes";
import { useUploadAttachments, useDeleteAttachment } from "@/hooks/useNoteAttachments";
import { useAuth } from "@/contexts/AuthContext";
import { ActivityTimeline } from "@/components/ui/activity-timeline";
import { lazy, Suspense } from "react";
import { TaskDetailHeader } from "@/components/tasks/task-detail-header";
const NoteComposer = lazy(() => import("@/components/notes/note-composer").then(m => ({ default: m.NoteComposer })));
const TaskDialog = lazy(() => import("@/components/tasks/task-dialog").then(m => ({ default: m.TaskDialog })));
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DetailPageSkeleton } from "@/components/ui/skeletons";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";

const TaskDetailPage = observer(function TaskDetailPage() {
  const bodyReady = useDeferredMount();
  const searchParams = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const { data: task, isLoading: taskLoading } = useTask(id);
  usePageTitle(task?.title ?? 'Task');
  const { userId } = useAuth();
  const timeline = useEntityTimeline("task", id);
  const timelineEntries = timeline.entries;
  const updateNote = useUpdateNote("task", id);
  const deleteNote = useDeleteNote("task", id);
  const uploadAttachments = useUploadAttachments("task", id);
  const deleteAttachment = useDeleteAttachment("task", id);
  const deleteTask = useDeleteTask();
  const router = useRouter();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const taskStore = useTaskStore();
  const userStore = useUserStore();
  const contactStore = useContactStore();
  const dealStore = useDealStore();
  const dealTypeStore = useDealTypeStore();
  const projectStore = useProjectStore();
  const saveField = (field: string) => (value: string) => {
    taskStore.update(id, { [field]: value || null });
  };
  const ownerOptions = userStore.all.map((u) => ({
    value: u.id,
    label: u.displayName || u.email,
  }));
  const contactOptions = useCallback(() => {
    const all = contactStore.all;
    const companies = all.filter((c) => c.type === ContactType.Company);
    const independentPersons = all.filter((c) => c.type === ContactType.Person && !c.companyId);
    const opts: { value: string; label: string }[] = [];
    for (const company of companies) {
      opts.push({ value: company.id, label: company.name });
      for (const emp of contactStore.getByCompanyId(company.id)) {
        opts.push({ value: emp.id, label: `  └ ${emp.name}` });
      }
    }
    for (const person of independentPersons) {
      opts.push({ value: person.id, label: person.name });
    }
    return opts;
  }, [contactStore])();
  const dealOptions = dealStore.all.map((d) => {
    const typeName = d.dealTypeName || dealTypeStore.getById(d.dealTypeId)?.name || d.dealTypeId;
    const label = d.contactName ? `${typeName} — ${d.contactName}` : typeName;
    return { value: d.id, label };
  });
  const statusOptions = [
    { value: "0", label: "Open" },
    { value: "1", label: "In Progress" },
    { value: "2", label: "Completed" },
  ];
  const priorityOptions = [
    { value: "0", label: "Low" },
    { value: "1", label: "Medium" },
    { value: "2", label: "High" },
  ];

  const linkedDeal = task?.dealId ? dealStore.getById(task.dealId) : undefined;
  const linkedProject = task?.projectId ? projectStore.getById(task.projectId) : undefined;
  const linkedContact = task?.contactId ? contactStore.getById(task.contactId) : undefined;

  if (!bodyReady || taskLoading) {
    return <DetailPageSkeleton />;
  }

  if (!task) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Task not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TaskDetailHeader task={task} onSaveTitle={(title) => taskStore.update(id, { title })} onDelete={() => setIsDeleteOpen(true)} />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* Main content: notes */}
        {/* Mobile-only bottom inset: the shell's scroll-container padding is not
            honored by Chromium flex scrolling, so clear the fixed bottom nav here. */}
        <div className="space-y-4 min-w-0 order-2 lg:order-1 pb-[calc(var(--mobile-bottom-nav-height)+env(safe-area-inset-bottom))] lg:pb-0">
          <NoteComposer entityType="task" entityId={id} />
          <ActivityTimeline
            entries={timelineEntries}
            isLoading={timeline.isLoading}
            hasMore={timeline.hasMore}
            onLoadMore={() => timeline.loadMore()}
            isLoadingMore={timeline.isLoadingMore}
            currentEntityId={id}
            onEditNote={(noteId, content) => updateNote.mutateAsync({ noteId, content })}
            onDeleteNote={(noteId) => deleteNote.mutateAsync(noteId)}
            onUploadFiles={(noteId, files) => uploadAttachments.mutateAsync({ noteId, files }).then(() => {})}
            onDeleteAttachment={(noteId, attachmentId) => deleteAttachment.mutate({ noteId, attachmentId })}
            currentUserId={userId ?? undefined}
          />
          <Button className="h-11 w-full lg:hidden" onClick={() => setIsEditDialogOpen(true)}>Edit task</Button>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 order-1 lg:order-2 lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Details</CardTitle>
                <Button size="sm" variant="ghost" className="h-8 px-2" aria-label="Edit task" onClick={() => setIsEditDialogOpen(true)}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <InlineSelectField
                label="Status"
                value={String(task.status)}
                options={statusOptions}
                onSave={(val) => taskStore.update(id, { status: Number(val) as CrmTaskStatus })}
                icon={<CircleDot className="h-4 w-4" />}
              />
              <InlineSelectField
                label="Priority"
                value={String(task.priority)}
                options={priorityOptions}
                onSave={(val) => taskStore.update(id, { priority: Number(val) as CrmTaskPriority })}
                icon={<Flag className="h-4 w-4" />}
              />
              <InlineDateField
                label="Due Date"
                value={task.dueDate ? task.dueDate.split("T")[0] : null}
                onSave={(v) => taskStore.update(id, { dueDate: v || null })}
                icon={<CalendarDays className="h-4 w-4" />}
                emptyText="Set due date…"
              />
              <InlineSelectField
                label="Contact"
                value={task.contactId ?? null}
                options={contactOptions}
                onSave={(val) => taskStore.update(id, { contactId: val || null })}
                icon={<User className="h-4 w-4" />}
                emptyText="No contact"
                placeholder="Select contact…"
                renderValue={(opt) =>
                  opt ? (
                    <Link
                      href={`/dashboard/contact?id=${opt.value}`}
                      className="text-sm hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {opt.label}
                    </Link>
                  ) : null
                }
              />
              <InlineSelectField
                label="Deal"
                value={task.dealId ?? null}
                options={dealOptions}
                onSave={(val) => taskStore.update(id, { dealId: val || null })}
                icon={<Handshake className="h-4 w-4" />}
                emptyText="No deal"
                placeholder="Select deal…"
                renderValue={(opt) =>
                  opt ? (
                    <Link
                      href={`/dashboard/deal?id=${opt.value}`}
                      className="text-sm hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {opt.label}
                    </Link>
                  ) : null
                }
              />
              <InlineField
                label="Description"
                value={task.description}
                onSave={saveField("description")}
                multiline
                icon={<AlignLeft className="h-4 w-4" />}
              />
              <InlineSelectField
                label="Owner"
                value={task.ownerId ?? null}
                options={ownerOptions}
                onSave={(val) => taskStore.update(id, { ownerId: val || null })}
                icon={<UserCheck className="h-4 w-4" />}
                emptyText="No owner"
                placeholder="Select owner…"
              />
            </CardContent>
          </Card>

          {task.dealId && (
            <LinkedRecordCard
              icon={<Handshake className="h-3.5 w-3.5" />}
              title="Linked deal"
              name={linkedDeal?.description || task.dealTypeName || "Deal"}
              subtitle={linkedDeal?.contactName ?? linkedDeal?.dealTypeName}
              href={`/dashboard/deal?id=${task.dealId}`}
            />
          )}
          {task.projectId && (
            <LinkedRecordCard
              icon={<FolderKanban className="h-3.5 w-3.5" />}
              title="Linked project"
              name={linkedProject?.name || task.projectName || "Project"}
              subtitle={
                linkedProject
                  ? `${linkedProject.dealsCount} deals · ${linkedProject.tasksCount} tasks`
                  : undefined
              }
              href={`/dashboard/project?id=${task.projectId}`}
            />
          )}
          {task.contactId && (
            <LinkedRecordCard
              icon={<User className="h-3.5 w-3.5" />}
              title="Linked contact"
              name={linkedContact?.name || task.contactName || "Contact"}
              subtitle={
                linkedContact
                  ? linkedContact.email ??
                    (linkedContact.type === ContactType.Company ? "Company" : "Person")
                  : undefined
              }
              href={`/dashboard/contact?id=${task.contactId}`}
            />
          )}
        </aside>
      </div>

      {isEditDialogOpen && <TaskDialog
        task={task}
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
      />}

      <DeleteConfirmation
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={() => { deleteTask.mutateAsync({ id, name: task.title }); router.push("/dashboard/tasks"); }}
        entityType="task"
        entityName={task.title}
      />
    </div>
  );
});


import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/task')({
  component: TaskDetailPage,
  pendingComponent: () => <DetailPageSkeleton />,
  pendingMs: 0,
  pendingMinMs: 150,
});
